/**
 * ============================================================================
 * DAY SUMMARY - Compact Daily Booking Statistics Bar
 * ============================================================================
 */

import { CURRENCY_SYMBOL } from '../utils/currency';
import React, { useMemo } from 'react';

// ============================================================================
// TYPES
// ============================================================================

interface Booking {
  id: number;
  type: 'spotts' | 'manual' | 'recurring' | 'blocked' | 'cancelled' | 'external';
  status?: string;
  rawData?: {
    final_price?: number | string;
    venue_share?: number | string;
    price?: number | string;
    amount?: number | string;
    payment_status?: string;
    status?: string;
  };
}

interface DaySummaryProps {
  selectedDate: Date;
  bookings: Booking[];
  isLoading?: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Format currency with commas (no abbreviations)
const formatCurrency = (amount: number): string => {
  return `${CURRENCY_SYMBOL}${amount.toLocaleString('en-NG', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

// Safely parse a number from various input types
const parseNumber = (value: unknown): number => {
  if (typeof value === 'number') return isNaN(value) ? 0 : value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(/[^0-9.-]/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const DaySummary: React.FC<DaySummaryProps> = ({
  bookings,
  isLoading = false,
}) => {
  // Calculate summary stats from bookings
  const stats = useMemo(() => {
    // Filter out blocked slots, external reservations, and cancelled bookings
    const activeBookings = bookings.filter(
      (b) =>
        b.type !== 'blocked' &&
        b.type !== 'external' &&
        b.type !== 'cancelled' &&
        b.status !== 'cancelled'
    );

    // SPOTTS bookings (includes 'spotts' and 'recurring' types)
    const spottsBookings = activeBookings.filter(
      (b) => b.type === 'spotts' || b.type === 'recurring'
    );

    // Manual bookings
    const manualBookings = activeBookings.filter((b) => b.type === 'manual');

    // Calculate revenue - safely parse all values as numbers
    const getRevenue = (booking: Booking): number => {
      const raw = booking.rawData;
      if (!raw) return 0;

      // For manual bookings, use price or amount
      if (booking.type === 'manual') {
        return parseNumber(raw.price) || parseNumber(raw.amount) || parseNumber(raw.venue_share) || 0;
      }

      // For SPOTTS bookings, use venue_share (what vendor actually gets)
      return parseNumber(raw.venue_share) || parseNumber(raw.final_price) || 0;
    };

    const spottsRevenue = spottsBookings.reduce((sum, b) => sum + getRevenue(b), 0);
    const manualRevenue = manualBookings.reduce((sum, b) => sum + getRevenue(b), 0);
    const totalRevenue = spottsRevenue + manualRevenue;

    return {
      total: activeBookings.length,
      spottsCount: spottsBookings.length,
      manualCount: manualBookings.length,
      totalRevenue,
      spottsRevenue,
      manualRevenue,
    };
  }, [bookings]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-2 mb-3 animate-pulse">
        <div className="flex gap-3">
          <div className="h-8 w-20 bg-gray-200 rounded"></div>
          <div className="h-8 w-24 bg-gray-200 rounded"></div>
          <div className="h-8 w-20 bg-gray-200 rounded"></div>
          <div className="h-8 w-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  // Don't show if no bookings
  if (stats.total === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-3 p-2 sm:p-3">
      <div className="flex flex-wrap items-center gap-2 sm:gap-4">
        {/* Total Bookings */}
        <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-lg">
          <span className="text-base sm:text-lg font-bold text-gray-900">{stats.total}</span>
          <span className="text-xs text-gray-500">bookings</span>
        </div>

        {/* Total Revenue */}
        <div className="flex items-center gap-1.5 px-2 py-1 bg-green-50 rounded-lg border border-green-100">
          <span className="text-sm sm:text-base font-bold text-green-700">{formatCurrency(stats.totalRevenue)}</span>
          <span className="text-xs text-green-600">total</span>
        </div>

        {/* SPOTTS */}
        {stats.spottsCount > 0 && (
          <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 rounded-lg border border-blue-100">
            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
            <span className="text-sm font-semibold text-blue-700">{stats.spottsCount}</span>
            <span className="text-xs text-blue-600">({formatCurrency(stats.spottsRevenue)})</span>
          </div>
        )}

        {/* Manual */}
        {stats.manualCount > 0 && (
          <div className="flex items-center gap-1.5 px-2 py-1 bg-green-50 rounded-lg border border-green-100">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <span className="text-sm font-semibold text-green-700">{stats.manualCount}</span>
            <span className="text-xs text-green-600">({formatCurrency(stats.manualRevenue)})</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DaySummary;
